import React from 'react';
import styled from 'styled-components';
import DayPicker from 'react-day-picker';
import moment from 'moment-timezone';

import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Input from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';

import TodayIcon from '@mui/icons-material/Today';

interface Props {
    name: string;
    label?: string;
    date?: Date | null | undefined;
    onChange?: (value: Date) => void;
    disabled?: boolean;
}

// Regex for month day format excluding the year. ie. MM-dd or MM.dd or MM/dd
const monthDayFormat = /^(0?[1-9]|1[012])[- \/ .](3[01]|0?[1-9]|[12][0-9])$/;

const formatDate = (d?: Date | null, dateFormat: string = 'MM/DD/YYYY') => {
    let momentDate;
    if (!d) {
        return '';
    } else {
        momentDate = moment({
            year: d.getUTCFullYear(),
            month: d.getUTCMonth(),
            date: d.getUTCDate(),
        });
    }
    if (dateFormat) {
        return momentDate.format(dateFormat);
    }
    return momentDate.format('MM/DD/YYYY');
};

const DebriefDatePicker: React.FC<Props> = props => {
    const [dateString, setDateString] = React.useState(formatDate(props.date));
    const [open, setOpen] = React.useState(false);
    const btnRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        setDateString(formatDate(props.date));
    }, [props.date]);

    const saveDate = (date: Date) => {
        props.onChange(date);
        setDateString(formatDate(props.date));
    };

    const handleCalendarClick = () => {
        setOpen(s => !s);
    };

    const handeClickAway = () => {
        setOpen(false);
    };

    const handleBlur = () => {
        if (!dateString || dateString.trim() === '') {
            setOpen(false);
        }
        let momentDate;
        if (dateString.match(monthDayFormat)) {
            const currentYear = new Date().getFullYear();
            const currentYearDateString = currentYear + '-' + dateString;
            const cleanedDateString = currentYearDateString.replace(
                /[. \/]/,
                '-'
            );
            momentDate = moment(cleanedDateString);
        } else {
            momentDate = moment(dateString);
        }
        if (!momentDate.isValid()) {
            props.onChange(null);
        } else {
            setOpen(false);
            const asDate = momentDate.toDate();
            saveDate(asDate);
        }
    };

    const handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
        setDateString(evt.target.value);
    };

    const handleDayClick = (date: Date) => {
        setOpen(false);
        saveDate(date);
    };

    return (
        <ClickAwayListener onClickAway={handeClickAway}>
            <FormControl
                ref={btnRef}
                variant="standard"
                className="date-selection"
                fullWidth
            >
                <InputLabel htmlFor={props.name}>{props.label}</InputLabel>
                <Input
                    id={props.name}
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={dateString}
                    endAdornment={
                        <InputAdornment position="end">
                            <IconButton onClick={handleCalendarClick}>
                                <TodayIcon />
                            </IconButton>
                        </InputAdornment>
                    }
                />
                <DayPickerPopper
                    sx={{
                        bgcolor: 'white',
                        border: '1px solid black',
                        zIndex: 10,
                    }}
                    anchorEl={btnRef.current}
                    open={open}
                    disablePortal
                >
                    <DayPicker
                        selectedDays={props.date}
                        initialMonth={props.date}
                        onDayClick={handleDayClick}
                        onTodayButtonClick={handleDayClick}
                        todayButton="Today"
                    />
                </DayPickerPopper>
            </FormControl>
        </ClickAwayListener>
    );
};

export default DebriefDatePicker;

const DayPickerPopper = styled(Popper)`
    overflow: visible;
    .DayPicker {
        display: inline-block;
        font-size: 13px;
    }

    .DayPicker-wrapper {
        position: relative;
        flex-direction: row;
        padding-bottom: 1em;
        user-select: none;
    }

    .DayPicker-Months {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
    }

    .DayPicker-Month {
        display: table;
        margin: 0 1em;
        margin-top: 1em;
        border-spacing: 0;
        border-collapse: collapse;
        user-select: none;
    }

    .DayPicker-NavButton {
        position: absolute;
        top: 1em;
        right: 1.5em;
        left: auto;
        display: inline-block;
        margin-top: 2px;
        width: 1.25em;
        height: 1.25em;
        background-position: center;
        background-size: 50%;
        background-repeat: no-repeat;
        color: #8b9898;
        cursor: pointer;
    }

    .DayPicker-NavButton:hover {
        opacity: 0.8;
    }

    .DayPicker-NavButton--prev {
        margin-right: 1.5em;
        background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACQAAAAwCAYAAAB5R9gVAAAABGdBTUEAALGPC/xhBQAAAVVJREFUWAnN2G0KgjAYwPHpGfRkaZeqvgQaK+hY3SUHrk1YzNLay/OiEFp92I+/Mp2F2Mh2lLISWnflFjzH263RQjzMZ19wgs73ez0o1WmtW+dgA01VxrE3p6l2GLsnBy1VYQOtVSEH/atCCgqpQgKKqYIOiq2CBkqtggLKqQIKgqgCBjpJ2Y5CdJ+zrT9A7HHSTA1dxUdHgzCqJIEwq0SDsKsEg6iqBIEoq/wEcVRZBXFV+QJxV5mBtlDFB5VjYTaGZ2sf4R9PM7U9ZU+lLuaetPP/5Die3ToO1+u+MKtHs06qODB2zBnI/jBd4MPQm1VkY79Tb18gB+C62FdBFsZR6yeIo1YQiLJWMIiqVjQIu1YSCLNWFgijVjYIuhYYCKoWKAiiFgoopxYaKLUWOii2FgkophYp6F3r42W5A9s9OcgNvva8xQaysKXlFytoqdYmQH6tF3toSUo0INq9AAAAAElFTkSuQmCC');
    }

    .DayPicker-NavButton--next {
        background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACQAAAAwCAYAAAB5R9gVAAAABGdBTUEAALGPC/xhBQAAAXRJREFUWAnN119ugjAcwPHWzJ1gnmxzB/BBE0n24m4xfNkTaOL7wOtsl3AXMMb+Vjaa1BG00N8fSEibPpAP3xAKKs2yjzTPH9RAjhEo9WzPr/Vm8zgE0+gXATAxxuxtqeJ9t5tIwv5AtQAApsfT6TPdbp+kUBcgVwvO51KqVhMkXKsVJFXrOkigVhCIs1Y4iKlWZxB1rX4gwlpRIIpa8SDkWmggrFq4IIRaJKCYWnSgnrXIQV1r8YD+1Vrn+bReagysIFfLABRt31v8oBu1xEBttfRbltmfjgEcWh9snUS2kNdBK6WN1vrOWxObWsz+fjxevsxmB1GQDfINWiev83nhaoiB/CoOU438oPrhXS0WpQ9xc1ZQWxWHqUYe0I0qrKCQKjygDlXIQV2r0IF6ViEBxVTBBSFUQQNhVYkHIVeJAtkNsbQ7c1LtzP6FsObhb2rCKv7NBIGoq4SDmKoEgTirXAcJVGkFSVVpgoSrXICGUMUH/QBZNSUy5XWUhwAAAABJRU5ErkJggg==');
    }

    .DayPicker-NavButton--interactionDisabled {
        display: none;
    }

    .DayPicker-Caption {
        display: table-caption;
        margin-bottom: 0.5em;
        padding: 0 0.5em;
        text-align: left;
    }

    .DayPicker-Caption > div {
        font-size: 16px;
        font-weight: 600;
        margin: 3px 0;
        color: #222;
    }

    .DayPicker-Weekdays {
        display: table-header-group;
        margin-top: 1em;
    }

    .DayPicker-WeekdaysRow {
        display: table-row;
    }

    .DayPicker-Weekday {
        display: table-cell;
        padding: 0.5em;
        color: #8b9898;
        text-align: center;
        font-size: 0.875em;
    }

    .DayPicker-Weekday abbr[title] {
        border-bottom: none;
        text-decoration: none;
    }

    .DayPicker-Body {
        display: table-row-group;
    }

    .DayPicker-Week {
        display: table-row;
    }

    .DayPicker-Day {
        display: table-cell;
        padding: 0.5em;
        border-radius: 2px;
        vertical-align: middle;
        text-align: center;
        cursor: pointer;
        color: #444;
        &:focus {
            background: #444;
            color: #fff;
        }
    }

    .DayPicker-WeekNumber {
        display: table-cell;
        padding: 0.5em;
        min-width: 1em;
        border-right: 1px solid #eaecec;
        color: #8b9898;
        vertical-align: middle;
        text-align: right;
        font-size: 0.75em;
        cursor: pointer;
    }

    .DayPicker--interactionDisabled .DayPicker-Day {
        cursor: default;
    }

    .DayPicker-Footer {
        padding-top: 0.5em;
    }

    .DayPicker-TodayButton {
        border: none;
        background-color: transparent;
        background-image: none;
        box-shadow: none;
        color: ${p => p.theme.blue};
        cursor: pointer;
        text-align: center;
        width: 100%;
        font-size: 13px;
        &:active,
        &:focus {
            background: transparent;
        }
    }

    /* Default modifiers */

    .DayPicker-Day--today {
        color: #d0021b;
        font-weight: 700;
    }

    .DayPicker-Day--outside {
        color: #8b9898;
        cursor: default;
    }

    .DayPicker-Day--disabled {
        color: #dce0e0;
        cursor: default;
    }

    /* Example modifiers */

    .DayPicker-Day--sunday {
        background-color: #f7f8f8;
    }

    .DayPicker-Day--sunday:not(.DayPicker-Day--today) {
        color: #dce0e0;
    }

    .DayPicker-Day--selected:not(.DayPicker-Day--disabled):not(.DayPicker-Day--outside) {
        position: relative;

        background-color: #3498db;
        color: #f0f8ff;
    }

    .DayPicker-Day--selected:not(.DayPicker-Day--disabled):not(.DayPicker-Day--outside):hover {
        background-color: #3498db;
    }

    .DayPicker:not(.DayPicker--interactionDisabled)
        .DayPicker-Day:not(.DayPicker-Day--disabled):not(.DayPicker-Day--selected):not(.DayPicker-Day--outside):hover {
        background-color: #f0f8ff;
    }

    /* DayPickerInput */

    .DayPickerInput {
        display: inline-block;
    }

    .DayPickerInput-OverlayWrapper {
        position: relative;
    }

    .DayPickerInput-Overlay {
        position: absolute;
        left: 0;
        z-index: 1;
        background: white;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
    }
`;
